import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled, { css, keyframes } from 'styled-components';

import type { DropCardInput } from '@sorare/core/src/__generated__/globalTypes';
import { fadeIn } from '@sorare/core/src/atoms/animations/keyframes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { SorareLogo } from '@sorare/core/src/atoms/icons/SorareLogo';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, BrandL, BrandS } from '@sorare/core/src/atoms/typography';
import { NewSeasonLogo } from '@sorare/core/src/components/Season2027/NewSeasonLogo';
import { Card } from '@sorare/core/src/components/card/Card';
import { droppedCard } from '@sorare/core/src/components/card/style';
import { FlyToTarget } from '@sorare/core/src/components/dialog/FlyToTarget/index.tsx';
import { sportProfile } from '@sorare/core/src/contexts/currentUser/queries';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import {
  BaseLayout,
  BottomArea,
} from '#src/components/commonCard/RevealPackCardsDialog/ui.ts';

import type {
  PackRecap_anyCardInterface,
  claimCardsFromPackMutation,
  claimCardsFromPackMutationVariables,
} from './__generated__/index.graphql.ts';

const cardEnterAnimation = keyframes`
  from {
    opacity: 0;
    transform: translateY(120px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Top = styled.div`
  grid-area: top;
  padding: var(--double-unit) var(--double-unit) 0;
`;
const Center = styled(Vertical).attrs({ center: true, gap: 4 })`
  grid-area: center;
  min-height: 0;
  align-self: stretch;
  justify-self: stretch;
  padding: 0 var(--double-unit);
`;
const Bottom = styled(BottomArea)`
  position: sticky;
  bottom: 0;
  justify-self: stretch;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--double-unit);
  padding: var(--double-unit);
  background: linear-gradient(
    to top,
    rgba(var(--c-rgb-black), 0.5) 40%,
    transparent
  );
`;
const titleStyle = css`
  opacity: 0;
  animation: ${fadeIn} 1s ease-in-out forwards;
`;
const TitleBlock = styled(Vertical).attrs({ center: true, gap: 2 })`
  ${titleStyle}
`;
const Subtitle = styled(BrandS)`
  ${titleStyle}
`;
const Cards = styled(Horizontal).attrs({ center: true })`
  flex-wrap: wrap;
  max-width: var(--100vw);
  --gap: var(--double-unit);
  gap: var(--gap);
`;
const CardImgWrapper = styled.div`
  width: calc(calc(33 * var(--vw)) - var(--gap));
  @media ${tabletAndAbove} {
    width: 120px;
  }
  & > img {
    max-width: 100%;
    max-height: 25vh;
    aspect-ratio: var(--card-aspect-ratio);
    object-fit: contain;
    position: relative;
  }
  &.dropped {
    ${droppedCard}
  }
  opacity: 0;
  transform: translateY(120px);
  animation: ${cardEnterAnimation} 0.8s calc(var(--index) * 0.05s)
    var(--ease-out-slide) forwards;
`;
const CLAIM_CARDS_FROM_PACK_MUTATION = gql`
  mutation claimCardsFromPackMutation(
    $input: claimCardsFromPackInput!
    $sport: Sport!
  ) {
    claimCardsFromPack(input: $input) {
      currentUser {
        slug
        ...CurrentUseProvider_sportProfile
      }
      errors {
        message
        code
      }
    }
  }
  ${sportProfile}
` as TypedDocumentNode<
  claimCardsFromPackMutation,
  claimCardsFromPackMutationVariables
>;

type Props = {
  cards: PackRecap_anyCardInterface[];
  onContinue: () => void;
  droppedCards: DropCardInput[];
  onBack: () => void;
  isFirstPack?: boolean;
  packId: string;
  onCardsClaimed?: () => void;
  continueLoading?: boolean;
  isBlockchainPack?: boolean;
};
export const PackRecap = ({
  cards,
  droppedCards,
  onContinue,
  onBack,
  isFirstPack,
  packId,
  onCardsClaimed,
  continueLoading,
  isBlockchainPack,
}: Props) => {
  const { sport } = useSportContext();

  const [claimCardsFromPack, { loading: claimCardsFromPackLoading }] =
    useMutation(CLAIM_CARDS_FROM_PACK_MUTATION, {
      showErrorsWithSnackNotification: true,
    });

  const keptCards: typeof cards = [];
  const scrappedCards: typeof cards = [];
  cards.forEach(c => {
    if (droppedCards.some(card => card.cardSlug === c.slug)) {
      scrappedCards.push(c);
    } else {
      keptCards.push(c);
    }
  });

  const claimAndContinue = async () => {
    await claimCardsFromPack({
      variables: {
        sport,
        input: {
          chosenCardSlugs: keptCards.map(c => c.slug),
          packId,
        },
      },
    });
    onCardsClaimed?.();
    onContinue();
  };

  const continueLoadingState = claimCardsFromPackLoading || continueLoading;

  return (
    <BaseLayout className="text-center">
      <Top>
        <TitleBlock>
          {isBlockchainPack ? <NewSeasonLogo size="medium" /> : <SorareLogo />}
          <BrandL
            className={isBlockchainPack ? undefined : 'text-color-contrast'}
          >
            <FormattedMessage
              defaultMessage="{count, plural, one {New signing} other {new signings}}"
              values={{
                count: cards.length,
              }}
            />
          </BrandL>
          {!!isBlockchainPack && (
            <BodyL>
              <FormattedMessage
                defaultMessage="Welcome {count} {count, plural, one {player} other {players}} to your team!"
                values={{ count: keptCards.length }}
              />
            </BodyL>
          )}
        </TitleBlock>
      </Top>
      <Center>
        {keptCards.length ? (
          <Cards>
            <FlyToTarget destination="card">
              {keptCards.map((card, index) => (
                <CardImgWrapper
                  key={card.slug}
                  style={{ '--index': index } as React.CSSProperties}
                >
                  <Card card={card} width={320} />
                </CardImgWrapper>
              ))}
            </FlyToTarget>
          </Cards>
        ) : (
          <BodyL color="var(--c-nd-600)" className="text-color-contrast">
            <FormattedMessage defaultMessage="All players in the pack were scrapped" />
          </BodyL>
        )}
        {!!scrappedCards.length && (
          <Vertical center gap={1.5}>
            <Subtitle className="text-color-contrast">
              <FormattedMessage defaultMessage="Scrapped Cards" />
            </Subtitle>
            <Cards>
              {scrappedCards.map((card, index) => (
                <CardImgWrapper
                  key={card.slug}
                  className="dropped"
                  style={{ '--index': index } as React.CSSProperties}
                >
                  <Card card={card} width={320} />
                </CardImgWrapper>
              ))}
            </Cards>
          </Vertical>
        )}
      </Center>
      <Bottom>
        {isBlockchainPack ? (
          <Button
            size="medium"
            color="quaternary"
            fullWidth
            disabled={continueLoadingState}
            onClick={claimAndContinue}
          >
            <FormattedMessage {...glossary.continue} />
          </Button>
        ) : (
          <>
            {!isFirstPack && (
              <Button
                color="secondary"
                onClick={onBack}
                size="medium"
                fullWidth
              >
                <FormattedMessage {...glossary.back} />
              </Button>
            )}
            <Button
              color="primary"
              onClick={claimAndContinue}
              size="medium"
              loading={continueLoadingState}
              fullWidth
            >
              <FormattedMessage {...glossary.continue} />
            </Button>
          </>
        )}
      </Bottom>
    </BaseLayout>
  );
};

PackRecap.fragments = {
  anyCardInterface: gql`
    fragment PackRecap_anyCardInterface on AnyCardInterface {
      slug
      previouslyOwnedCard
      cardSet {
        slug
      }
      ...Card_anyCard
    }
    ${Card.fragments.anyCard}
  ` as TypedDocumentNode<PackRecap_anyCardInterface>,
};
